/* eslint-disable unicorn/no-nested-ternary */

import {
  DescriptionList,
  type DescriptionListItem,
  Icon,
  Inline,
  List,
  Notification,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import { useCompany } from '@omnipresentgroup/domains';
import type { Company } from '@omnipresentgroup/types';
import { useMemo } from 'react';

import { DirectDebitDetails } from './DirectDebitDetails';
import { MFADetails } from './MFADetails';

type CompanyDetailsPermissions = {
  canViewMFA?: boolean;
  canEditMFA?: boolean;
};

const getCompanyEntries = (permissions?: CompanyDetailsPermissions) => {
  const baseEntries = [
    {
      key: 'name',
      label: 'Full legal name',
    },
    {
      key: 'countryOfRegistration',
      label: 'Country of registration',
    },
    {
      key: 'registeredAddress',
      label: 'Registered address',
    },
    {
      key: 'vatNumber',
      label: 'Company VAT number (if applicable)',
    },
    {
      key: 'companyNumber',
      label: 'Company registration number',
    },
    {
      key: 'contactName',
      label: 'Primary contact name',
    },
    {
      key: 'contactPhoneNumber',
      label: 'Primary contact phone',
    },
    {
      key: 'invoicingEmail',
      label: 'Invoice email',
    },
    ...(permissions?.canViewMFA
      ? [
          {
            key: 'enforceMFA',
            label: (
              <Inline gap="4" align="center">
                <Typography as="span" size="14" weight="medium">
                  Multi-factor authentication
                </Typography>

                <Tooltip
                  id="mfa-tooltip"
                  description="This setting will enforce MFA for all managers in this company."
                >
                  <Icon icon="InfoCircle" size="16" />
                </Tooltip>
              </Inline>
            ),
          },
        ]
      : []),
    {
      key: 'directDebit',
      label: 'Direct Debit',
    },
  ];

  return baseEntries;
};

const rendererMapping: {
  [key: string]: (
    company: Company,
    permissions?: CompanyDetailsPermissions,
  ) => string | JSX.Element;
} = {
  contactPhoneNumber: (company: Company) =>
    company?.primaryContact?.phoneNumber ?? '',
  companyNumber: (company: Company) =>
    company?.identifiers?.companyNumber ?? '',
  contactName: (company: Company) => company?.primaryContact?.name ?? '',
  registeredAddress: (company: Company) => {
    const items = Object.values(company?.registeredAddress || {})
      .filter(Boolean)
      .map((item) => ({
        id: item as string,
        name: item as string,
      }));

    return (
      <List
        direction="column"
        gap="4"
        items={items}
        keyExtractor="id"
        render={({ name }: { id: string; name: string }) => (
          <Typography as="span" size="14">
            {name}
          </Typography>
        )}
      />
    );
  },
  vatNumber: (company: Company) => company?.identifiers?.vatNumber ?? '',
  enforceMFA: (company: Company, permissions?: CompanyDetailsPermissions) => {
    const { enforceMFA } = company?.settings || {};
    return (
      <MFADetails
        companyId={company?.id}
        enforceMFA={enforceMFA}
        canEdit={permissions?.canEditMFA}
      />
    );
  },
  directDebit: (company: Company) => (
    <DirectDebitDetails companyId={company?.id} />
  ),
};

const getCompanyDetails = (
  company: Company,
  isLoading: boolean,
  permissions?: CompanyDetailsPermissions,
): DescriptionListItem[] => {
  const entries = getCompanyEntries(permissions);

  return entries.map(({ label, key }) => ({
    header: label,
    detail: isLoading ? (
      <Skeleton key={key} height={24} />
    ) : typeof rendererMapping[key] === 'function' ? (
      rendererMapping[key](company, permissions)
    ) : (
      (company?.[key as keyof Company] as string | JSX.Element)
    ),
  }));
};

export const CompanyDetails = ({
  companyId,
  hideEditNotification = false,
  permissions,
}: {
  companyId: string;
  hideEditNotification?: boolean;
  permissions?: CompanyDetailsPermissions;
}) => {
  const { data: company, isLoading } = useCompany({
    id: companyId,
    enabled: !!companyId,
  });

  const companyDetails = useMemo(
    () => getCompanyDetails(company as Company, isLoading, permissions),
    [company, isLoading],
  );

  return (
    <Stack gap="16">
      {!hideEditNotification && (
        <Notification
          id="edit_company_details_notification"
          title="Editing company information"
          link={{
            text: 'Contact us',
            to: 'mailto:help@omnipresent.com',
          }}
          description="If you need to update your company's information, please contact us."
        />
      )}
      <DescriptionList
        fraction="1/2"
        id="company-details-list"
        list={companyDetails}
      />
    </Stack>
  );
};
